import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { landing as landingApi, wallet as walletApi } from '@/lib/api';
import PublicNav, { PublicFooter } from '@/components/PublicNav';
import { ArrowRight, ArrowLeft, Clock, Loader2, Brain, Database, Radio, HardDrive, Search, GitBranch, Gauge, Box, Cpu, FileText, Workflow, Zap, Code, Check } from 'lucide-react';
import { toast } from 'sonner';

const svcIcons = { qdrant: Search, elasticsearch: Database, neo4j: GitBranch, redis: Gauge, kafka: Radio, minio: Box, bge_m3: Brain, bge_reranker: Workflow, minilm: Cpu, spacy: FileText, vane_search: Search, searxng: Database, lightpanda_browser: Cpu, pdf_parser: FileText };
const catColors = { ml_model: 'from-violet-500 to-purple-600', database: 'from-blue-500 to-indigo-600', streaming: 'from-orange-500 to-red-600', storage: 'from-emerald-500 to-teal-600', ai_search: 'from-pink-500 to-rose-600' };
const catLabels = { ml_model: 'ML Model', database: 'Database', streaming: 'Streaming', storage: 'Storage' };
const tierStyles = { economy: { label: 'Economy', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' }, standard: { label: 'Standard', color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200' }, priority: { label: 'Priority', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' } };

export default function FeaturePage() {
  const { serviceId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [allServices, setAllServices] = useState({});
  const [footer, setFooter] = useState({});
  const [loading, setLoading] = useState('');

  useEffect(() => {
    landingApi.feature(serviceId).then(r => setData(r.data)).catch(() => navigate('/features'));
    landingApi.pricing().then(r => setAllServices(r.data?.services || {})).catch(() => {});
    landingApi.get().then(r => setFooter(r.data?.footer || {})).catch(() => {});
  }, [serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-8 h-8 border-2 brand-spinner rounded-full animate-spin" /></div>;

  const fp = data.feature_page || {};
  const Icon = svcIcons[serviceId] || Cpu;
  const gradient = catColors[data.category] || 'brand-gradient-bg';

  return (
    <div data-testid={`feature-page-${serviceId}`} className="light-page min-h-screen bg-white text-slate-900">
      <PublicNav />

      {/* Hero */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16 px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"><div className="absolute top-0 left-1/4 w-[500px] h-[500px] brand-mesh-1 rounded-full blur-[120px] opacity-40" /></div>
        <div className="max-w-4xl mx-auto relative">
          <Link to="/features" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6"><ArrowLeft className="w-3.5 h-3.5" />All Services</Link>
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>{data.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{catLabels[data.category] || data.category}</span>
                <span className="text-sm text-slate-500">{fp.tagline || data.friendly_description}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="pb-12 px-5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>What is {data.name}?</h2>
          <div className="text-slate-600 leading-relaxed space-y-3">
            {(fp.overview || data.description || '').split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* AI Development Use Cases */}
      {(fp.ai_development || []).length > 0 && (
        <section className="py-12 px-5 bg-slate-50 border-y border-slate-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-slate-900 mb-6" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>How it helps in AI development</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fp.ai_development.map((uc, i) => (
                <div key={i} className="p-5 rounded-xl bg-white border border-slate-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 brand-text" />
                    <h3 className="font-bold text-slate-900">{uc.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{uc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it works on our platform */}
      {fp.how_it_works && (
        <section className="py-12 px-5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>How it works on our platform</h2>
            <div className="p-6 rounded-2xl brand-bg-muted brand-border-soft" style={{borderWidth: '1px'}}>
              <div className="text-slate-600 leading-relaxed space-y-3">
                {fp.how_it_works.split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {['No Docker', 'No port configuration', 'No credentials to manage', 'Auto-namespaced', 'Just HTTP API calls'].map(t => (
                  <span key={t} className="flex items-center gap-1 px-3 py-1 rounded-full bg-white brand-border-soft brand-text text-xs font-medium" style={{borderWidth: '1px'}}><Check className="w-3 h-3" />{t}</span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      <section className="py-12 px-5 bg-slate-50 border-y border-slate-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>Pricing</h2>
          <div className="grid grid-cols-3 gap-4">
            {['economy', 'standard', 'priority'].map(tier => {
              const p = data.pricing?.[tier];
              const ts = tierStyles[tier];
              return p ? (
                <div key={tier} className={`p-5 rounded-xl ${ts.bg} border ${ts.border} text-center`}>
                  <p className={`text-xs font-bold uppercase tracking-wider ${ts.color} mb-1`}>{ts.label}</p>
                  <p className="text-3xl font-extrabold text-slate-900 font-mono">${p.hourly_rate}</p>
                  <p className="text-xs text-slate-500">/hour</p>
                  <p className="text-[11px] text-slate-400 mt-2 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />{'\u2264'}{p.timeout_seconds}s response</p>
                </div>
              ) : null;
            })}
          </div>
          <div className="mt-4 text-center">
            <button onClick={() => user ? navigate('/dashboard/billing') : navigate('/signup')}
              className="brand-btn inline-flex items-center gap-2 px-6 py-2.5 text-sm">
              Add Credit & Start Using <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Works great with */}
      {(fp.works_with || []).length > 0 && (
        <section className="py-12 px-5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-slate-900 mb-4" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>Works great with</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {fp.works_with.map(relId => {
                const rel = allServices[relId];
                if (!rel) return null;
                const RelIcon = svcIcons[relId] || Cpu;
                const relGrad = catColors[rel.category] || 'brand-gradient-bg';
                return (
                  <Link key={relId} to={`/features/${relId}`} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:brand-border-soft hover:shadow-sm transition-all">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${relGrad} flex items-center justify-center shrink-0`}><RelIcon className="w-4 h-4 text-white" /></div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{rel.name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{rel.friendly_description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <PublicFooter footer={footer} />
    </div>
  );
}
